import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { db } from '@/lib/firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { Phone, User, Stethoscope, MessageSquare, Trash2, Plus, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface Examination {
  id: string;
  patientName: string;
  phone: string;
  serviceType: string;
  doctorNotes?: string;
  paymentStatus: 'paid' | 'pending' | 'transferred';
  createdAt: Date;
}

export default function Examinations() {
  const { toast } = useToast();
  const [examinations, setExaminations] = useState<Examination[]>([]);
  const [isAddingExamination, setIsAddingExamination] = useState(false);
  const [newExamination, setNewExamination] = useState({
    patientName: '',
    phone: '',
    serviceType: '',
    doctorNotes: '',
    paymentStatus: 'pending' as const
  });

  const services = [
    "تنظيف الأسنان",
    "حشو الأسنان",
    "علاج الجذور",
    "تقويم الأسنان",
    "زراعة الأسنان",
    "تبييض الأسنان",
    "خلع الأسنان",
    "تركيب الأسنان",
    "علاج اللثة",
    "استشارة عامة"
  ];

  const fetchExaminations = async () => {
    try {
      const examinationsRef = collection(db, 'examinations');
      const q = query(examinationsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const examinationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      })) as Examination[];
      setExaminations(examinationsData);
    } catch (error) {
      console.error('Error fetching examinations:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الكشوفات"
      });
    }
  };

  useEffect(() => {
    fetchExaminations();
  }, []);

  const handleAddExamination = async () => {
    if (!newExamination.patientName || !newExamination.phone || !newExamination.serviceType) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة"
      });
      return;
    }

    try {
      await addDoc(collection(db, 'examinations'), {
        ...newExamination,
        createdAt: new Date()
      });

      toast({
        title: "تمت الإضافة",
        description: "تم إضافة الكشف بنجاح"
      });

      setNewExamination({
        patientName: '',
        phone: '',
        serviceType: '',
        doctorNotes: '',
        paymentStatus: 'pending'
      });
      setIsAddingExamination(false);
      fetchExaminations();
    } catch (error) {
      console.error('Error adding examination:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة الكشف"
      });
    }
  };

  const handlePaymentStatusChange = async (examinationId: string, newStatus: 'paid' | 'pending' | 'transferred') => {
    try {
      const examinationRef = doc(db, 'examinations', examinationId);
      await updateDoc(examinationRef, { paymentStatus: newStatus });
      toast({
        title: "تم التحديث",
        description: "تم تحديث حالة الدفع بنجاح"
      });
      fetchExaminations();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة الدفع"
      });
    }
  };

  const handleDelete = async (examinationId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الكشف؟')) return;

    try {
      const examinationRef = doc(db, 'examinations', examinationId);
      await deleteDoc(examinationRef);
      toast({
        title: "تم الحذف",
        description: "تم حذف الكشف بنجاح"
      });
      fetchExaminations();
    } catch (error) {
      console.error('Error deleting examination:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الكشف"
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <Card className="shadow-modern">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
              كشوفات العيادة
            </CardTitle>
            <Dialog open={isAddingExamination} onOpenChange={setIsAddingExamination}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  إضافة كشف جديد
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>إضافة كشف جديد</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>اسم المريض</Label>
                    <Input
                      value={newExamination.patientName}
                      onChange={(e) => setNewExamination(prev => ({ ...prev, patientName: e.target.value }))}
                      placeholder="أدخل اسم المريض"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>رقم الهاتف</Label>
                    <Input
                      value={newExamination.phone}
                      onChange={(e) => setNewExamination(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="أدخل رقم الهاتف"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>نوع الخدمة</Label>
                    <Select
                      value={newExamination.serviceType}
                      onValueChange={(value) => setNewExamination(prev => ({ ...prev, serviceType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر نوع الخدمة" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>ملاحظات الطبيب</Label>
                    <Textarea
                      value={newExamination.doctorNotes}
                      onChange={(e) => setNewExamination(prev => ({ ...prev, doctorNotes: e.target.value }))}
                      placeholder="أدخل ملاحظات الطبيب"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>حالة الدفع</Label>
                    <Select
                      value={newExamination.paymentStatus}
                      onValueChange={(value: 'paid' | 'pending' | 'transferred') => 
                        setNewExamination(prev => ({ ...prev, paymentStatus: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر حالة الدفع" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">مدفوع</SelectItem>
                        <SelectItem value="pending">باقي عليه</SelectItem>
                        <SelectItem value="transferred">تم تحويله</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddExamination} className="w-full">
                    إضافة الكشف
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6">
              {examinations.map((examination) => (
                <Card key={examination.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg">{examination.patientName}</CardTitle>
                      </div>
                      <Badge
                        variant={
                          examination.paymentStatus === 'paid'
                            ? 'default'
                            : examination.paymentStatus === 'transferred'
                            ? 'secondary'
                            : 'destructive'
                        }
                      >
                        {examination.paymentStatus === 'paid'
                          ? 'مدفوع'
                          : examination.paymentStatus === 'transferred'
                          ? 'تم تحويله'
                          : 'باقي عليه'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{examination.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Stethoscope className="w-4 h-4 text-muted-foreground" />
                        <span>{examination.serviceType}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(examination.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}
                      </div>
                      {examination.doctorNotes && (
                        <div className="flex items-start gap-2 pt-2 border-t">
                          <MessageSquare className="w-4 h-4 text-muted-foreground mt-1" />
                          <span className="text-muted-foreground">{examination.doctorNotes}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                      <Select
                        value={examination.paymentStatus}
                        onValueChange={(value: 'paid' | 'pending' | 'transferred') =>
                          handlePaymentStatusChange(examination.id, value)
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="تغيير حالة الدفع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">مدفوع</SelectItem>
                          <SelectItem value="pending">باقي عليه</SelectItem>
                          <SelectItem value="transferred">تم تحويله</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDelete(examination.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 